import { Box, Button } from '@cambridgeassessment/cambridge-ui';
import React, { memo, useCallback, useState } from 'react';
import PasswordField from 'src/components/PasswordField/PasswordField';
import useAuth from 'src/hooks/useAuth';
import useNotifications from 'src/hooks/useNotifications';
import { PASSWORD_STATUS_TEXT } from 'src/services/gigya/constants';
import { changePassword } from 'src/services/gigya/gigya-service';
import { GACategory } from 'src/types';
import { sendGAEvent } from 'src/utils/googleAnalytics';
import { mapErrorCodeToNotificationParams } from 'src/utils/mapErrorCodeToNotificationParams';
import CreatePasswordField from '../CreatePasswordField/CreatePasswordField';
import Loader from '../Loader/Loader';
import { useStyles } from './styles';

const ChangePasswordForm: React.FC = () => {
  const [currentPassword, setCurrentPassword] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [isNewPasswordValid, setIsNewPasswordValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuth();

  const classes = useStyles();

  const { addNotification } = useNotifications();

  const {
    user: { hasPassword: hasCurrentPassword },
    update,
  } = useAuth();

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setCurrentPassword(event.target.value);
    },
    []
  );

  const handleNewChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setNewPassword(event.target.value);
    },
    []
  );

  const handleNotification = useCallback(
    (errorCode: number) => {
      const notificationParams = mapErrorCodeToNotificationParams(errorCode);
      addNotification(notificationParams);
    },
    [addNotification]
  );

  const handleSubmit = useCallback(
    async (event) => {
      event?.preventDefault();
      setIsLoading(true);
      sendGAEvent(
        GACategory.personalInfo,
        `${user.roles} clicked on action Save password`
      );
      try {
        await changePassword(
          currentPassword.trim(),
          newPassword.trim(),
          (response) => {
            handleNotification(response.errorCode);
            update();
          }
        );
      } catch (err) {
        addNotification({
          title: PASSWORD_STATUS_TEXT['internal_api_error'],
          variant: 'error',
        });
      } finally {
        setCurrentPassword('');
        setNewPassword('');
        setIsLoading(false);
      }
    },
    [
      addNotification,
      currentPassword,
      handleNotification,
      newPassword,
      update,
      user.roles,
    ]
  );

  if (isLoading) {
    return <Loader height={300} />;
  }

  return (
    <Box className={classes.form}>
      {hasCurrentPassword && (
        <PasswordField
          password={currentPassword}
          onChange={handleChange}
          label="Current password"
          data-testid="changePasswordForm-currentPassword"
        />
      )}
      <CreatePasswordField
        password={newPassword}
        onChange={handleNewChange}
        setIsValid={setIsNewPasswordValid}
        data-testid="changePasswordForm-newPassword"
      />
      <Box className={classes.buttons}>
        <Button
          className={classes.saveButton}
          color="primary"
          disabled={
            (hasCurrentPassword && currentPassword?.length === 0) ||
            !isNewPasswordValid
          }
          data-testid="changePasswordForm-save"
          onClick={handleSubmit}
        >
          Save password
        </Button>
      </Box>
    </Box>
  );
};

export default memo(ChangePasswordForm);
